import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';
import LandingNav from '../components/Layout/LandingNav';
import LandingFooter from '../components/Layout/LandingFooter';

function CookiePolicy() {
  const { theme } = useTheme();
  const d = theme === 'dark';

  return (
    <div className={`min-h-screen flex flex-col ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Cookie Policy"
        description="How PaperBrainz uses cookies and similar technologies on our AI customer support chatbot platform."
        path="/cookie-policy"
      />
      <LandingNav />

      <div className="flex-1 pt-32 sm:pt-40 pb-24 sm:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20 max-w-2xl mx-auto">
            <h1 className={`text-5xl md:text-6xl lg:text-[64px] font-semibold tracking-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Cookie Policy
            </h1>
            <p className={`text-lg md:text-xl font-light leading-relaxed ${d ? 'text-gray-500' : 'text-gray-500'}`}>
              How we use cookies and similar technologies
            </p>
            <p className={`text-sm font-light mt-4 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              Last updated: March 26, 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-14 sm:space-y-16">
            <div className="space-y-14 sm:space-y-16">

              {/* What are cookies */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  What are Cookies?
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  Cookies are small text files that are placed on your computer or mobile device when you visit our website. They are widely used to make websites work more efficiently and provide information to website owners.
                </p>
              </section>

              {/* How we use cookies */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  How We Use Cookies
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We use cookies for the following purposes:
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Essential Cookies</h4>
                    <p className="text-sm text-gray-500">
                      These are necessary for the website to function properly, such as authentication and security features.
                    </p>
                  </div>

                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Performance Cookies</h4>
                    <p className="text-sm text-gray-500">
                      These help us understand how visitors interact with our website by collecting anonymous information.
                    </p>
                  </div>

                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Functionality Cookies</h4>
                    <p className="text-sm text-gray-500">
                      These enable enhanced functionality like remembering your language preferences, theme settings, and user configuration.
                    </p>
                  </div>

                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Analytics Cookies</h4>
                    <p className="text-sm text-gray-500">
                      These help us analyze website performance and improve our services based on usage patterns.
                    </p>
                  </div>
                </div>
              </section>

              {/* Types of cookies */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Types of Cookies We Use
                </h2>

                <div className="space-y-6">
                  <div className={`border-l-2 pl-4 ${d ? 'border-white/[0.1]' : 'border-gray-200'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Session Cookies</h4>
                    <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      These are temporary cookies that remain in your browser until you leave the website. They enable you to navigate between pages efficiently and help maintain your login session.
                    </p>
                  </div>

                  <div className={`border-l-2 pl-4 ${d ? 'border-white/[0.1]' : 'border-gray-200'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Persistent Cookies</h4>
                    <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      These cookies remain on your device for a set period of time or until you delete them. They help us remember your preferences for future visits, such as your dashboard theme (light/dark mode) and widget customization settings.
                    </p>
                  </div>

                  <div className={`border-l-2 pl-4 ${d ? 'border-white/[0.1]' : 'border-gray-200'}`}>
                    <h4 className={`font-semibold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Third-Party Cookies</h4>
                    <p className={`mb-3 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      We may use third-party services that place cookies on your device, such as:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-500">
                      <li>Lemon Squeezy for secure payment processing</li>
                      <li>Analytics services for website performance analysis</li>
                      <li>Content delivery networks for improved performance</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Specific cookies used */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Specific Cookies We Use
                </h2>

                <div className="overflow-x-auto">
                  <table className={`w-full border-collapse overflow-hidden rounded-[16px] ${d ? 'border border-white/[0.06]' : 'border border-gray-200'}`}>
                    <thead className={`${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-sm font-medium border-b ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'}`}>Cookie Name</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium border-b ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'}`}>Purpose</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium border-b ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'}`}>Duration</th>
                      </tr>
                    </thead>
                    <tbody className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3 font-mono text-sm">accessToken</td>
                        <td className="px-4 py-3 text-sm">Authentication and session management</td>
                        <td className="px-4 py-3 text-sm">Session</td>
                      </tr>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3 font-mono text-sm">refreshToken</td>
                        <td className="px-4 py-3 text-sm">Maintain login state securely</td>
                        <td className="px-4 py-3 text-sm">30 days</td>
                      </tr>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3 font-mono text-sm">theme</td>
                        <td className="px-4 py-3 text-sm">Remember your light/dark mode preference</td>
                        <td className="px-4 py-3 text-sm">1 year</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-mono text-sm">_ls_*</td>
                        <td className="px-4 py-3 text-sm">Lemon Squeezy payment processing and checkout</td>
                        <td className="px-4 py-3 text-sm">Session</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Managing cookies */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Managing Cookies
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  You can control and manage cookies in various ways:
                </p>

                <div className="space-y-4">
                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Browser Settings</h4>
                    <p className="text-sm text-gray-500">
                      Most browsers allow you to view, delete, and block cookies through their settings. You can typically find these options in your browser's "Privacy" or "Security" settings.
                    </p>
                  </div>

                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Third-Party Tools</h4>
                    <p className="text-sm text-gray-500">
                      There are various third-party browser extensions and tools available to help you manage cookies across websites.
                    </p>
                  </div>

                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Opt-Out Links</h4>
                    <p className="text-sm text-gray-500">
                      Some third-party services provide direct opt-out mechanisms. Check with individual service providers for their specific opt-out processes.
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mt-4">
                  Please note: Disabling certain cookies may affect the functionality of our website and limit your ability to use some features, including authentication and dashboard access.
                </p>
              </section>

              {/* Chat Widget Cookies */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Chat Widget Cookies
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  When you embed the PaperBrainz chat widget on your website, it may use cookies for the following purposes:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li><span className={`${d ? 'text-gray-300' : 'text-gray-700'}`}>Session identification:</span> To maintain conversation context for visitors</li>
                  <li><span className={`${d ? 'text-gray-300' : 'text-gray-700'}`}>Widget state:</span> To remember if the widget is open or minimized</li>
                  <li><span className={`${d ? 'text-gray-300' : 'text-gray-700'}`}>User preferences:</span> To store any user-selected preferences within the chat</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  As a PaperBrainz customer, you are responsible for disclosing the use of the chat widget and any associated cookies in your own privacy and cookie policies.
                </p>
              </section>

              {/* Updates */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Updates to This Policy
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
                </p>
              </section>

              {/* Summary */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Cookie Summary
                </h2>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <ul className="space-y-3">
                    {[
                      'We use cookies to improve your experience',
                      'Essential cookies are required for core functionality',
                      'You can manage cookies through your browser settings',
                      "We don't use cookies for advertising purposes",
                      'Third-party cookies are used for payments and analytics only'
                    ].map((item, i) => (
                      <li key={i} className={`flex items-center ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className={`w-1 h-1 rounded-full mr-3 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Contact Information */}
              <section className={`border-t pt-8 ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Contact Us
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  If you have any questions about our use of cookies, please contact us at:
                </p>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Email</h4>
                      <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                        <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                          support@paperbrainz.com
                        </a>
                      </p>
                    </div>
                    <div>
                      <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Company</h4>
                      <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                        PaperBrainz Privacy Team<br />
                        <span className="text-gray-500">IE Aleksandr Kirakosyan</span>
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}

export default CookiePolicy;
